/**
 * Fills this device's mail cache for the Settings "Keep mail for" window: pages older
 * list rows per account (GET /api/mail?before=), then stores message bodies so threads
 * open from disk. Runs quietly in the background and pauses while Hub is hidden.
 */

import { api, ApiError, type MailAccount, type MailThread } from "@/lib/api";
import {
  mailCacheWindowCutoff,
  readCachedMailDetail,
  readCachedMailList,
  writeCachedMailDetail,
  writeCachedMailList,
} from "@/mail/offlineCache";
import { isMailThreadHydrated, markThreadDetailExpanded } from "@/mail/threadHydrate";

const SYNC_STATE_KEY = "atw-hub-mail-window-sync";
const FOLDERS = ["inbox", "sent"] as const;
const PAGE_GAP_MS = 400;
const BODY_GAP_MS = 250;
/** ATW Mail bodies come from our Stalwart engine, which rate-limits bursts from mail.atw.net. */
const ATW_MAIL_BODY_GAP_MS = 1000;
const MAX_PAGES_PER_LIST = 400;
/** A list that already reached the window is re-paged at most this often. */
const RECHECK_MS = 12 * 60 * 60 * 1000;

type SyncState = Record<string, { coveredTo: number; syncedAt: number }>;

export type MailWindowSyncOptions = {
  accounts: MailAccount[];
  rowKeyOf: (thread: MailThread) => string;
  apiIdOf: (thread: MailThread) => string;
  normalizeList: (payload: unknown) => MailThread[];
  normalizeDetail: (row: MailThread, payload: unknown) => MailThread;
  /** A cached list for this account/folder (and "all") just grew. */
  onListUpdated: (folder: string, accountId: string) => void;
  isCancelled: () => boolean;
};

let running = false;

function readState(): SyncState {
  try {
    const parsed = JSON.parse(localStorage.getItem(SYNC_STATE_KEY) || "{}") as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as SyncState) : {};
  } catch {
    return {};
  }
}

function writeState(state: SyncState) {
  try {
    localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

async function waitUntilVisible(isCancelled: () => boolean) {
  while (document.visibilityState === "hidden" && !isCancelled()) await sleep(2000);
}

function timeMs(thread: MailThread): number | null {
  const t = new Date(thread.date || "").getTime();
  return Number.isNaN(t) ? null : t;
}

function mergeUnique(
  base: MailThread[],
  extra: MailThread[],
  rowKeyOf: (thread: MailThread) => string,
): MailThread[] {
  const seen = new Set(base.map(rowKeyOf));
  const merged = [...base];
  for (const thread of extra) {
    const key = rowKeyOf(thread);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(thread);
  }
  return merged.sort((a, b) => (timeMs(b) ?? 0) - (timeMs(a) ?? 0));
}

async function backfillList(
  opts: MailWindowSyncOptions,
  account: MailAccount,
  folder: string,
  cutoff: number | null,
  state: SyncState,
) {
  const stateKey = `${account.id}:${folder}`;
  const prior = state[stateKey];
  const target = cutoff ?? 0;
  if (prior && prior.coveredTo <= target && Date.now() - prior.syncedAt < RECHECK_MS) return;

  const own = (await readCachedMailList({ account: account.id, folder })) || [];
  const all = (await readCachedMailList({ account: "all", folder })) || [];
  let list = mergeUnique(
    own,
    all.filter((thread) => thread.accountId === account.id),
    opts.rowKeyOf,
  );
  const times = list.map(timeMs).filter((t): t is number => t != null);
  let cursor = times.length ? Math.min(...times) : Date.now();
  let reachedEnd = false;

  for (let page = 0; page < MAX_PAGES_PER_LIST; page += 1) {
    await waitUntilVisible(opts.isCancelled);
    if (opts.isCancelled()) return;
    if (cutoff != null && cursor <= cutoff) break;
    const params = new URLSearchParams({
      folder,
      account: account.id,
      before: new Date(cursor).toISOString(),
      limit: "50",
    });
    let rows: MailThread[];
    try {
      rows = opts.normalizeList(await api(`/api/mail?${params}`, { timeoutMs: 45_000 }));
    } catch {
      return;
    }
    if (!rows.length) {
      reachedEnd = true;
      break;
    }
    const pageTimes = rows.map(timeMs).filter((t): t is number => t != null);
    const nextCursor = pageTimes.length ? Math.min(...pageTimes) : cursor;
    if (nextCursor >= cursor) {
      reachedEnd = true;
      break;
    }
    cursor = nextCursor;
    list = mergeUnique(list, rows, opts.rowKeyOf);
    await writeCachedMailList({ account: account.id, folder }, list, undefined, { quiet: true });
    const allNow = (await readCachedMailList({ account: "all", folder })) || [];
    await writeCachedMailList(
      { account: "all", folder },
      mergeUnique(allNow, rows, opts.rowKeyOf),
      undefined,
      { quiet: true },
    );
    opts.onListUpdated(folder, account.id);
    await sleep(PAGE_GAP_MS);
  }

  state[stateKey] = { coveredTo: reachedEnd ? 0 : cursor, syncedAt: Date.now() };
  writeState(state);
}

async function prefetchBodies(opts: MailWindowSyncOptions, cutoff: number | null) {
  let threads: MailThread[] = [];
  for (const folder of FOLDERS) {
    for (const account of opts.accounts) {
      const rows = (await readCachedMailList({ account: account.id, folder })) || [];
      threads = mergeUnique(threads, rows, opts.rowKeyOf);
    }
  }
  const providerOf = new Map(opts.accounts.map((account) => [account.id, account.providerId || ""]));
  // One failing account (rate limit, engine down) must not be retried thread by thread.
  const stoppedAccounts = new Set<string>();
  for (const thread of threads) {
    await waitUntilVisible(opts.isCancelled);
    if (opts.isCancelled()) return;
    const accountKey = thread.accountId || "";
    if (stoppedAccounts.has(accountKey)) continue;
    const t = timeMs(thread);
    if (cutoff != null && t != null && t < cutoff) continue;
    const rowKey = opts.rowKeyOf(thread);
    const existing = await readCachedMailDetail(rowKey);
    if (existing && isMailThreadHydrated(existing)) continue;
    const apiId = opts.apiIdOf(thread);
    if (!apiId) continue;
    try {
      const payload = await api(
        `/api/mail/${encodeURIComponent(apiId)}${
          thread.accountId ? `?accountId=${encodeURIComponent(thread.accountId)}` : ""
        }`,
        { timeoutMs: 30_000 },
      );
      const detail = markThreadDetailExpanded(opts.normalizeDetail(thread, payload));
      if (isMailThreadHydrated(detail)) await writeCachedMailDetail(rowKey, detail);
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 404)) stoppedAccounts.add(accountKey);
    }
    await sleep(providerOf.get(accountKey) === "atw-mail" ? ATW_MAIL_BODY_GAP_MS : BODY_GAP_MS);
  }
}

/** One pass at a time per tab; safe to call on every Mail mount. */
export async function syncMailWindow(opts: MailWindowSyncOptions): Promise<void> {
  if (running || !opts.accounts.length) return;
  running = true;
  try {
    const cutoff = mailCacheWindowCutoff();
    const state = readState();
    for (const folder of FOLDERS) {
      for (const account of opts.accounts) {
        if (opts.isCancelled()) return;
        await backfillList(opts, account, folder, cutoff, state);
      }
    }
    if (opts.isCancelled()) return;
    await prefetchBodies(opts, cutoff);
  } finally {
    running = false;
  }
}
